// ./src/components/features/Connectors/ConfigEditors/AzureBlobConnectorConfigEditor.tsx
import React, { useState, useEffect, useCallback } from "react";
import { Stack, TextInput, Text, Anchor, Alert, Code } from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { stringifyObject, parseObject } from "@/utils";
import { AzureBlobConnectorConfig } from "@/types";

export interface AzureBlobConnectorConfigEditorProps {
  /** JSON string of connector configuration */
  value: string;

  /** Callback when configuration changes */
  onChange: (value: string) => void;

  /** Whether the editor is disabled */
  disabled?: boolean;
}

// Default empty configuration
const DEFAULT_CONFIG: AzureBlobConnectorConfig = {
  connectionString: "",
  containerName: "",
  pathPrefix: "",
  fileNamePattern: "{{requestId}}.json",
};

/**
 * Parse and validate Azure Blob connector configuration
 */
const parseAzureBlobConfig = (jsonString: string): AzureBlobConnectorConfig => {
  const parsed = parseObject(jsonString, DEFAULT_CONFIG);
  return parsed;
};

export const AzureBlobConnectorConfigEditor: React.FC<
  AzureBlobConnectorConfigEditorProps
> = ({ value, onChange, disabled = false }) => {
  // Parse incoming value to config state
  const [config, setConfig] = useState<AzureBlobConnectorConfig>(() =>
    parseAzureBlobConfig(value)
  );

  // Sync config when external value changes
  useEffect(() => {
    const parsed = parseAzureBlobConfig(value);
    setConfig(parsed);
  }, [value]);

  // Update parent when config changes
  const updateConfig = useCallback(
    (newConfig: AzureBlobConnectorConfig) => {
      setConfig(newConfig);
      onChange(stringifyObject(newConfig, DEFAULT_CONFIG));
    },
    [onChange]
  );

  // Handle connection string change
  const handleConnectionStringChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, connectionString: e.target.value });
  };

  // Handle container name change
  const handleContainerNameChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    // Azure container names must be lowercase
    const containerName = e.target.value.toLowerCase();
    updateConfig({ ...config, containerName });
  };

  // Handle path prefix change
  const handlePathPrefixChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateConfig({ ...config, pathPrefix: e.target.value });
  };

  // Handle file name pattern change
  const handleFileNamePatternChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    updateConfig({ ...config, fileNamePattern: e.target.value });
  };

  // Validate container name
  const containerNameError =
    config.containerName &&
    !/^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/.test(config.containerName) &&
    config.containerName.length >= 3
      ? "Container name must be 3-63 characters, lowercase letters, numbers, and hyphens only"
      : undefined;

  return (
    <Stack gap="md">
      {/* Help Alert */}
      <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
        <Text size="sm">
          Find your connection string in{" "}
          <Anchor
            href="https://portal.azure.com"
            target="_blank"
            rel="noopener noreferrer"
          >
            Azure Portal
          </Anchor>
          {" → Storage Account → Access keys → Connection string"}
        </Text>
      </Alert>

      {/* Connection String Field */}
      <TextInput
        label="Connection String"
        placeholder="DefaultEndpointsProtocol=https;AccountName=..."
        description="Azure Storage account connection string"
        value={config.connectionString}
        onChange={handleConnectionStringChange}
        disabled={disabled}
        required
        size="md"
        type="password"
        autoComplete="off"
      />

      {/* Container Name Field */}
      <TextInput
        label="Container Name"
        placeholder="form-submissions"
        description="Name of the blob container (must already exist)"
        value={config.containerName}
        onChange={handleContainerNameChange}
        disabled={disabled}
        required
        size="md"
        error={containerNameError}
      />

      {/* Path Prefix Field */}
      <TextInput
        label="Path Prefix"
        placeholder="submissions/{{formName}}/"
        description="Optional folder path for organizing blobs"
        value={config.pathPrefix || ""}
        onChange={handlePathPrefixChange}
        disabled={disabled}
        size="md"
      />

      {/* File Name Pattern Field */}
      <TextInput
        label="File Name Pattern"
        placeholder="{{requestId}}.json"
        description="Pattern for naming files"
        value={config.fileNamePattern || "{{requestId}}.json"}
        onChange={handleFileNamePatternChange}
        disabled={disabled}
        size="md"
      />

      {/* Placeholders Help */}
      <Alert icon={<IconInfoCircle size={16} />} color="gray" variant="light">
        <Text size="sm" fw={500} mb={4}>
          Available placeholders:
        </Text>
        <Text size="xs" component="div">
          <Code>{"{{requestId}}"}</Code> — Submission ID (e.g.,
          20250530143022-ABCD)
          <br />
          <Code>{"{{formId}}"}</Code> — Form UUID
          <br />
          <Code>{"{{formName}}"}</Code> — Form name (sanitized)
          <br />
          <Code>{"{{timestamp}}"}</Code> — Unix timestamp
          <br />
          <Code>{"{{date}}"}</Code> — Date (YYYY-MM-DD)
        </Text>
      </Alert>
    </Stack>
  );
};
